// Ollama-format adapter API client (roadmap item 38).
//
// The Ollama adapter mounts under `/ollama/<project>/<repo>/v2/...` for
// the OCI distribution endpoints that the Ollama CLI talks to. The two
// browser-facing helpers below sit on the parallel `/ollama/<project>/
// <repo>/models` surface — JSON-shaped data the UI renders for an
// operator inspecting which models a given repo holds.
//
// Both helpers deliberately key on slugs (project + repo) rather than on
// repository UUIDs because the underlying registry surface is slug-keyed
// for protocol-compatibility with the Ollama CLI; the UI converts a
// `Repo` from `useRepoDetail` into the slug pair via `useProjects()`.

import useSWR from 'swr';

export interface OllamaLayer {
  media_type: string;
  digest: string;
  size: number;
}

export interface OllamaModelEntry {
  model: string;
  tag: string;
  digest: string;
  total_size_bytes: number;
  layer_count: number;
  uploaded_at: string;
  media_type?: string;
}

export interface OllamaModelsResponse {
  project: string;
  repo: string;
  models: OllamaModelEntry[];
  pull_hint: string;
}

export interface OllamaModelDetailResponse {
  project: string;
  repo: string;
  model: string;
  tag: string;
  digest: string;
  size_bytes: number;
  uploaded_at: string;
  metadata: Record<string, unknown>;
  pull_hint: string;
}

const fetcher = (url: string) =>
  fetch(url, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  });

export function useOllamaModels(projectSlug?: string, repoSlug?: string) {
  const url =
    projectSlug && repoSlug
      ? `/ollama/${encodeURIComponent(projectSlug)}/${encodeURIComponent(repoSlug)}/models`
      : null;
  return useSWR<OllamaModelsResponse>(url, fetcher);
}

export function useOllamaModelDetail(
  projectSlug: string | undefined,
  repoSlug: string | undefined,
  model: string | undefined,
  tag: string | undefined,
) {
  // Model names can contain slashes (e.g. `library/llama3`); the path
  // walks past the wildcard so the segments must be encoded individually
  // and then joined.
  const url =
    projectSlug && repoSlug && model && tag
      ? `/ollama/${encodeURIComponent(projectSlug)}/${encodeURIComponent(repoSlug)}/models/${model
          .split('/')
          .map(encodeURIComponent)
          .join('/')}/${encodeURIComponent(tag)}`
      : null;
  return useSWR<OllamaModelDetailResponse>(url, fetcher);
}
